import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { Language } from './landingCopy';
import { couponsCopy, BUSINESS_TYPES, PARTNERS_FALLBACK, SEQUENCES, PER_PERSON_LIMITS } from './couponsCopy';
import type { BusinessType, Sequence, PerPersonLimit } from './couponsCopy';
import Wordmark from './Wordmark';
import SiteBrand, { COUPONS_PATH, HOME_PATH, couponsLanguage } from './SiteBrand';
import './home.css';
import './coupons.css';

/*
  www.city-seek.com/coupons: CitySeek Coupons, told to the people who give the
  offers. It replaced the separate city-seek.store site, which now forwards
  here, so this is the one page a café, bar or activity is sent to.

  It is built from the landing page's parts (home.css) so the two read as one
  site, and adds its own sections in coupons.css. Every way in ends at the
  partner form, which posts to the same endpoint as the form inside the app.

  Never name what a business pays here. See couponsCopy.ts.
*/

const platform = 'https://www.city-seek.com';
const API = 'https://web-server.up.railway.app';
const LANGS: Language[] = ['en', 'de', 'tr'];
const languageNames: Record<Language, string> = { en: 'English', de: 'Deutsch', tr: 'Türkçe' };
const appLanguage: Record<Language, string> = { en: 'english', de: 'german', tr: 'turkish' };
const ANCHORS = ['how', 'offer', 'for', 'faq'];

const IMG = {
 door: '/brand/photos/coupons-door.webp',
 garden: '/brand/photos/coupons-garden.webp',
 inside: '/brand/photos/coupons-popup-inside.webp',
 list: '/brand/photos/coupons-app-coupons.webp',
 map: '/brand/photos/coupons-app-map.webp',
};

/* Keep a short last word on the line before it (same rule as the landing page). */
const tie = (text: string) => text.replace(/ · /g, ' · ').replace(/ (\S{1,8})$/, ' $1');

const app = (path: string, language: Language) => `${platform}${path}${path.includes('?') ? '&' : '?'}hl=${language}`;

function rememberLanguage(language: Language, chosen: boolean) {
 try {
  localStorage.setItem('cityseek:language', appLanguage[language]);
  if (chosen) localStorage.setItem('cityseek:languageChosen', '1');
 } catch { /* private mode */ }
}

type Plan = 'pair' | 'standard' | 'custom';
type PairDeal = 'half' | 'free';
type Live = { partners: string[]; coupons: number; places: number };

const Check = () => (
 <svg className="cp-check" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3.5 8.4 6.6 11.4 12.6 4.8" /></svg>
);

export default function CouponsPage({ language: initialLanguage = 'en' }: { language?: Language }) {
 const [language, setLanguage] = useState<Language>(initialLanguage);
 const c = couponsCopy[language];

 const pickLanguage = (next: Language) => {
  rememberLanguage(next, true);
  setLanguage(next);
  window.history.pushState({}, '', COUPONS_PATH[next] + window.location.hash);
 };
 useEffect(() => {
  const onPop = () => setLanguage(couponsLanguage(window.location.pathname));
  window.addEventListener('popstate', onPop);
  return () => window.removeEventListener('popstate', onPop);
 }, []);

 const [menuOpen, setMenuOpen] = useState(false);
 const menuButton = useRef<HTMLButtonElement>(null);
 useEffect(() => {
  document.documentElement.lang = language;
  document.title = c.title;
  rememberLanguage(language, false);
  const onKey = (e: KeyboardEvent) => {
   if (e.key !== 'Escape') return;
   setMenuOpen(open => { if (open) menuButton.current?.focus(); return false; });
  };
  document.addEventListener('keydown', onKey);
  return () => document.removeEventListener('keydown', onKey);
 }, [language, c.title]);

 /*
   Who is on the map today, from the same public catalogue the app reads.
   The prerendered page shows the partners we had at launch; the live list
   replaces them once it arrives, and the count only appears when it is real.
 */
 const [live, setLive] = useState<Live | null>(null);
 useEffect(() => {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), 6000);
  fetch(`${API}/api/store/products?city=Darmstadt`, { signal: ctrl.signal })
   .then(r => (r.ok ? r.json() : null))
   .then(data => {
    type Product = { partner_name?: string | null; inventory_quantity?: number | null };
    const products = ((data?.products || []) as Product[]).filter(p => p.partner_name);
    if (!products.length) return;
    // "Lowbrow" and "Lowbrow x Red Barn" are one place to a reader: keep the longer name.
    const names = [...new Set(products.map(p => p.partner_name!.trim()))];
    const partners = names.filter(n => !names.some(o => o !== n && o.toLowerCase().startsWith(n.toLowerCase() + ' ')));
    const open = products.filter(p => p.inventory_quantity == null || p.inventory_quantity > 0);
    setLive({ partners, coupons: open.length, places: new Set(open.map(p => p.partner_name!.trim())).size });
   })
   .catch(() => { /* the launch list stands in */ })
   .finally(() => window.clearTimeout(timer));
  return () => { window.clearTimeout(timer); ctrl.abort(); };
 }, []);
 const partners = live?.partners || PARTNERS_FALLBACK;

 /* ---------- The partner form ---------- */
 const [type, setType] = useState<BusinessType | ''>('');
 const [plan, setPlan] = useState<Plan>('pair');
 const [pairDeal, setPairDeal] = useState<PairDeal>('half');
 const [sequence, setSequence] = useState<Sequence>('10-15-20');
 const [limit, setLimit] = useState<PerPersonLimit>('none');
 const [status,setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
 const [typeMissing, setTypeMissing] = useState(false);
 const [sentTo, setSentTo] = useState('');
 const typeGroup = useRef<HTMLFieldSetElement>(null);
 const formTop = useRef<HTMLDivElement>(null);

 const goToForm = (next?: BusinessType, nextPlan?: Plan) => {
  if (next) { setType(next); setTypeMissing(false); }
  if (nextPlan) setPlan(nextPlan);
  if (status === 'sent') setStatus('idle');
  setMenuOpen(false);
  document.getElementById('partner')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
 };

 async function submit(event: FormEvent<HTMLFormElement>) {
  event.preventDefault();
  if (status === 'sending') return;
  if (!type) {
   setTypeMissing(true);
   typeGroup.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
   typeGroup.current?.querySelector('input')?.focus({ preventScroll: true });
   return;
  }
  const f = new FormData(event.currentTarget);
  const text = (name: string) => String(f.get(name) || '').trim();
  setStatus('sending');
  try {
   const response = await fetch(`${API}/api/store/partner-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
     businessType: type,
     businessName: text('businessName'),
     address: text('address'),
     contactName: text('contactName'),
     role: text('role'),
     email: text('email'),
     phone: text('phone'),
     link: text('link'),
     plan,
     sequence: plan === 'standard' ? sequence : undefined,
     pairDeal: plan === 'pair' ? pairDeal : undefined,
     pairItem: plan === 'pair' ? text('pairItem') : undefined,
     offer: plan === 'custom' ? text('offer') : undefined,
     perPersonLimit: limit,
     notes: text('notes'),
     consent: f.get('consent') === 'on',
     language: appLanguage[language],
     website: text('website'),
    }),
   });
   const result = await response.json().catch(() => null);
   if (!response.ok || !result?.success) throw new Error('not accepted');
   setSentTo(text('email'));
   setStatus('sent');
   window.requestAnimationFrame(() => formTop.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));
  } catch {
   setStatus('error');
  }
 }

 /*
   The phone-sized "become a partner" bar: shown once the hero is out of view,
   hidden again while the form itself is on screen.
 */
 const [sticky, setSticky] = useState(false);
 useEffect(() => {
  if (typeof IntersectionObserver === 'undefined') return;
  const hero = document.getElementById('cp-hero');
  const form = document.getElementById('partner');
  if (!hero || !form) return;
  let heroVisible = true; let formVisible = false;
  const io = new IntersectionObserver(entries => {
   entries.forEach(e => { if (e.target === hero) heroVisible = e.isIntersecting; else formVisible = e.isIntersecting; });
   setSticky(!heroVisible && !formVisible);
  });
  io.observe(hero); io.observe(form);
  return () => io.disconnect();
 }, []);

 const busy = status === 'sending';
 const words = c.compareWords as Record<string, string>;
 // With a limit, a step-up coupon's better deal waits for the next period, so say so.
 // The fixed 10% has no better deal, so it only gets the plain limit sentence.
 const stepsUp = plan === 'standard' && sequence !== '10';
 const limitHint = stepsUp && limit !== 'none'
  ? `${c.limitHints[limit]} ${c.limitStepHints[limit]}`
  : c.limitHints[limit];
 const sequenceHint = sequence === '10' ? `${c.fixedHint} ${c.fixedCoinRule}` : `${c.sequenceHint} ${c.coinRule}`;

 return <div className="cs cp">
  <a className="cs-skip" href="#main">{c.skip}</a>

  <header className="cs-header">
   <div className="cs-wrap cs-header-inner">
    <SiteBrand language={language} current="coupons" />
    <nav className="cs-nav" aria-label={c.menu}>
     {c.nav.map((label, i) => <a key={label} href={`#${ANCHORS[i]}`}>{label}</a>)}
    </nav>
    <div className="cs-header-actions">
     <div className="cs-lang" role="group" aria-label={c.languages}>
      {LANGS.map(code => (
       <button key={code} type="button" lang={code} aria-label={languageNames[code]}
        aria-pressed={code === language} onClick={() => pickLanguage(code)}>{code.toUpperCase()}</button>
      ))}
     </div>
     <a className="cs-btn cs-btn-small cs-header-cta" href="#partner">{c.navCta}</a>
     <button ref={menuButton} type="button" className="cs-menu-toggle" aria-expanded={menuOpen}
      aria-controls="cp-mobile-nav" onClick={() => setMenuOpen(!menuOpen)}>
      <svg className="cs-menu-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">{menuOpen ? <path d="M5 5l10 10M15 5 5 15" /> : <path d="M3 6h14M3 10h14M3 14h14" />}</svg>
      <span className="cs-menu-label">{menuOpen ? c.close : c.menu}</span>
     </button>
    </div>
   </div>
   <nav id="cp-mobile-nav" className="cs-mobile-nav" hidden={!menuOpen} aria-label={c.menu}>
    <div className="cs-wrap">
     {c.nav.map((label, i) => <a key={label} href={`#${ANCHORS[i]}`} onClick={() => setMenuOpen(false)}>{label}</a>)}
     <a href={HOME_PATH[language]} onClick={() => setMenuOpen(false)}>{c.forEveryone}</a>
     <div className="cs-lang cs-lang-menu" role="group" aria-label={c.languages}>
      {LANGS.map(code => (
       <button key={code} type="button" lang={code} aria-pressed={code === language} onClick={() => pickLanguage(code)}>{languageNames[code]}</button>
      ))}
     </div>
     <a className="cs-btn" href="#partner" onClick={() => setMenuOpen(false)}>{c.navCta}</a>
    </div>
   </nav>
  </header>

  <main id="main">
   {/* ---------- Hero ---------- */}
   <section className="cp-hero" id="cp-hero" aria-labelledby="cp-hero-title">
    <div className="cs-wrap cp-hero-grid">
     <div className="cp-hero-copy">
      <p className="cs-label">{tie(c.heroLabel)}</p>
      <h1 id="cp-hero-title">
       <span>{c.headline[0]}</span>
       <span className="cs-hero-accent">
        {c.headline[1]}
        <svg className="cs-hero-route" viewBox="0 0 400 40" preserveAspectRatio="none" aria-hidden="true" focusable="false">
         <defs>
          <mask id="cp-route-reveal" maskUnits="userSpaceOnUse">
           <path className="cs-route-reveal" d="M6 26 C 80 6, 140 38, 210 22 S 330 8, 394 24" pathLength="1" />
          </mask>
         </defs>
         <path mask="url(#cp-route-reveal)" className="cs-route-line" d="M6 26 C 80 6, 140 38, 210 22 S 330 8, 394 24" />
        </svg>
       </span>
      </h1>
      <p className="cp-hero-intro">{tie(c.intro)}</p>
      <div className="cs-hero-actions">
       <a className="cs-btn" href="#partner">{c.primary}</a>
       <a className="cs-btn cs-btn-ghost" href="#how">{c.secondary}</a>
      </div>
      <ul className="cp-promises">
       {c.promises.map(p => <li key={p}><Check />{p}</li>)}
      </ul>
     </div>

     <div className="cp-stage">
      <figure className="cp-stage-photo">
       <img src={IMG.door} width="1400" height="932" alt={c.heroPhotoAlt} fetchPriority="high" />
      </figure>
      <div className="cp-phone">
       <img src={IMG.list} width="540" height="1161" alt={c.heroPhoneAlt} />
      </div>
      <div className="cp-scan" aria-hidden="true">
       <span className="cp-scan-tick"><Check /></span>
       <span><strong>{c.scanTitle}</strong><span>{c.scanLine}</span></span>
      </div>
      <p className="cp-coins" aria-hidden="true"><span className="cp-coin-dot" />{c.coinsChip}</p>
     </div>
    </div>
   </section>

   {/* ---------- Who is already on the map ---------- */}
   <section className="cp-live" aria-labelledby="cp-live-title">
    <div className="cs-wrap cp-live-inner">
     <div className="cp-live-head">
      <h2 className="cs-label" id="cp-live-title">{c.liveLabel}</h2>
      {live && live.coupons > 0 && (
       <p className="cp-live-count"><span className="cs-live-dot" aria-hidden="true" />
        {c.liveCount.replace('{c}', String(live.coupons)).replace('{p}', String(live.places))}</p>
      )}
     </div>
     <ul className="cp-partners">{partners.map(name => <li key={name}>{name}</li>)}</ul>
     <a className="cs-inline-link" href={app('/app/coupons?view=map', language)}>{c.liveLink}</a>
    </div>
   </section>

   {/* ---------- Why ---------- */}
   <section className="cs-section" aria-labelledby="cp-why-title">
    <div className="cs-wrap">
     <div className="cs-heading">
      <p className="cs-label">{c.whyLabel}</p>
      <h2 id="cp-why-title">{tie(c.whyTitle)}</h2>
     </div>
     <ol className="cp-why">
      {c.why.map(([title, text], i) => (
       <li key={title}>
        <span className="cp-num" aria-hidden="true">0{i + 1}</span>
        <h3>{tie(title)}</h3>
        <p>{tie(text)}</p>
       </li>
      ))}
     </ol>
    </div>
   </section>

   {/* ---------- How it works ---------- */}
   <section className="cs-section cs-how cp-how" id="how" aria-labelledby="cp-how-title">
    <div className="cs-wrap">
     <div className="cs-heading">
      <p className="cs-label">{c.howLabel}</p>
      <h2 id="cp-how-title">{tie(c.howTitle)}</h2>
     </div>
     <div className="cs-steps-wrap">
      <svg className="cs-steps-route" viewBox="0 0 1000 60" preserveAspectRatio="none" aria-hidden="true" focusable="false">
       <path d="M125 30 C 220 -2, 300 62, 375 30 S 540 -2, 625 30 S 790 62, 875 30" />
      </svg>
      <ol className="cs-steps cp-steps">
       {c.steps.map(([title, text], i) => (
        <li key={title}>
         <span className="cs-step-dot" aria-hidden="true">{i + 1}</span>
         <h3>{tie(title)}</h3>
         <p>{tie(text)}</p>
        </li>
       ))}
      </ol>
     </div>
    </div>
   </section>

   {/* ---------- What guests see ---------- */}
   <section className="cs-section cp-see" aria-labelledby="cp-see-title">
    <div className="cs-wrap cp-see-grid">
     <div className="cp-see-copy">
      <p className="cs-label">{c.seeLabel}</p>
      <h2 id="cp-see-title">{tie(c.seeTitle)}</h2>
      <p className="cs-lead">{tie(c.seeText)}</p>
      <dl className="cp-see-points">
       {c.seePoints.map(([title, text]) => <div key={title}><dt>{tie(title)}</dt><dd>{tie(text)}</dd></div>)}
      </dl>
     </div>
     <div className="cp-shots">
      <figure className="cp-phone is-tilt-left">
       <img src={IMG.list} width="540" height="1161" alt={c.heroPhoneAlt} loading="lazy" />
       <figcaption>{c.shotList}</figcaption>
      </figure>
      <figure className="cp-phone is-tilt-right">
       <img src={IMG.map} width="540" height="1161" alt={c.shotMapAlt} loading="lazy" />
       <figcaption>{c.shotMap}</figcaption>
      </figure>
     </div>
    </div>
   </section>

   {/* ---------- Offers ---------- */}
   <section className="cs-section cp-offer" id="offer" aria-labelledby="cp-offer-title">
    <div className="cs-wrap">
     <div className="cs-heading">
      <p className="cs-label">{c.offerLabel}</p>
      <h2 id="cp-offer-title">{tie(c.offerTitle)}</h2>
      <p>{tie(c.offerIntro)}</p>
     </div>

     <div className="cp-offers">
      <article className="cp-offer-card is-featured">
       <div className="cp-offer-top">
        <span className="cp-offer-tag">{c.standardTag}</span>
        <span className="cp-offer-badge">{c.pairBadge}</span>
       </div>
       <h3>{c.pairName}</h3>
       <div className="cp-pair" aria-hidden="true">
        {c.pairVisual.map(([n, label], i) => (
         <div key={n} className={`cp-cup${i ? ' is-second' : ''}`}><strong>{n}</strong><span>{label}</span></div>
        ))}
       </div>
       <p>{tie(c.pairText)}</p>
       <ul className="cp-rules">{c.pairRules.map(r => <li key={r}><Check />{r}</li>)}</ul>
       <div className="cp-gain"><strong>{c.gainLabel}</strong><p>{tie(c.pairGain)}</p></div>
       <button type="button" className="cs-btn" onClick={() => goToForm(undefined, 'pair')}>{c.primary}</button>
      </article>

      <article className="cp-offer-card">
       <div className="cp-offer-top">
        <span className="cp-offer-tag">{c.standardTag}</span>
        <span className="cp-offer-badge">{c.seriesBadge}</span>
       </div>
       <h3>{c.seriesName}</h3>
       {/* The step-up as a timeline: what the same person pays with, visit after visit. */}
       <ol className="cp-ladder">
        {c.seriesLadder.map(([pct, when, note]) => (
         <li key={pct}><strong>{pct}</strong><span><span className="cp-ladder-when">{when}</span>{note}</span></li>
        ))}
       </ol>
       <p>{tie(c.seriesText)}</p>
       <p className="cp-offer-aside">{tie(c.seriesShort)}</p>
       <p className="cp-offer-aside">{tie(c.seriesFixed)}</p>
       <ul className="cp-rules">{[c.comboRule, c.coinRule, ...c.seriesRules].map(r => <li key={r}><Check />{r}</li>)}</ul>
       <div className="cp-gain"><strong>{c.gainLabel}</strong><p>{tie(c.seriesGain)}</p></div>
       <button type="button" className="cs-btn cs-btn-ghost" onClick={() => goToForm(undefined, 'standard')}>{c.primary}</button>
      </article>

      <article className="cp-offer-card">
       <div className="cp-offer-top"><span className="cp-offer-tag is-quiet">{c.ownTag}</span></div>
       <h3>{c.ownName}</h3>
       <p>{tie(c.ownText)}</p>
       <ul className="cp-examples">{c.ownExamples.map(x => <li key={x}>{x}</li>)}</ul>
       <div className="cp-gain"><strong>{c.gainLabel}</strong><p>{tie(c.ownGain)}</p></div>
       <button type="button" className="cs-btn cs-btn-ghost" onClick={() => goToForm(undefined, 'custom')}>{c.primary}</button>
      </article>
     </div>

     {/* Limit per person: open by default, the same choice the form offers for every plan. */}
     <div className="cp-limit">
      <div className="cp-limit-copy">
       <p className="cp-limit-label">{c.limitLabel}</p>
       <h3>{tie(c.limitTitle)}</h3>
       <p>{tie(c.limitText)}</p>
      </div>
      <div className="cp-limit-side">
       <ul className="cp-limit-scale">
        {c.limitScale.map(([option, note]) => (
         <li key={option} className={note ? 'is-default' : undefined}><span>{option}</span>{note && <span className="cp-limit-note">{note}</span>}</li>
        ))}
       </ul>
       <p className="cp-limit-series">{tie(c.limitSeries)}</p>
       <p className="cp-rule-line"><Check />{c.comboRule}</p>
      </div>
     </div>

     <div className="cp-offer-foot">
      <aside className="cp-tip">
       <strong>{c.tipTitle}</strong>
       <p>{tie(c.tipText)}</p>
      </aside>
      <figure className="cp-example">
       <figcaption>{c.exampleTitle}</figcaption>
       <dl>{c.example.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
      </figure>
     </div>
    </div>
   </section>

   {/* ---------- Who it's for ---------- */}
   <section className="cs-section cp-for" id="for" aria-labelledby="cp-for-title">
    <div className="cs-wrap">
     <div className="cs-heading">
      <p className="cs-label">{c.forLabel}</p>
      <h2 id="cp-for-title">{tie(c.forTitle)}</h2>
      <p>{c.forHint}</p>
     </div>
     <ul className="cp-types">
      {BUSINESS_TYPES.filter(t => t !== 'other').map(t => (
       <li key={t}>
        <button type="button" onClick={() => goToForm(t)}>
         <strong>{c.types[t][0]}</strong>
         <span>{c.types[t][1]}</span>
        </button>
       </li>
      ))}
     </ul>
    </div>
   </section>

   {/* ---------- Compared ---------- */}
   <section className="cs-section cp-compare" aria-labelledby="cp-compare-title">
    <div className="cs-wrap">
     <div className="cs-heading">
      <p className="cs-label">{c.compareLabel}</p>
      <h2 id="cp-compare-title">{tie(c.compareTitle)}</h2>
     </div>
     <div className="cp-table-wrap">
      <table className="cp-table">
       <thead>
        <tr><td />{c.compareCols.map((col, i) => <th key={col} scope="col" className={i === 3 ? 'is-us' : undefined}>{col}</th>)}</tr>
       </thead>
       <tbody>
        {c.compareRows.map(([row, ...cells]) => (
         <tr key={row}>
          <th scope="row">{row}</th>
          {cells.map((cell, i) => (
           <td key={i} className={`is-${cell}${i === 3 ? ' is-us' : ''}`} data-label={c.compareCols[i]}>
            <span className="cp-mark" aria-hidden="true" />{words[cell]}
           </td>
          ))}
         </tr>
        ))}
       </tbody>
      </table>
     </div>
     <p className="cp-table-note">{c.compareNote}</p>
    </div>
   </section>

   {/* ---------- Community ---------- */}
   <section className="cs-section cp-community" aria-labelledby="cp-community-title">
    <div className="cs-wrap cp-community-grid">
     <div className="cp-community-copy">
      <p className="cs-label">{c.communityLabel}</p>
      <h2 id="cp-community-title">{tie(c.communityTitle)}</h2>
      <p className="cs-lead">{tie(c.communityText)}</p>
      <dl className="cp-stats">
       {c.stats.map(([value, label]) => <div key={label}><dt>{tie(label)}</dt><dd>{value}</dd></div>)}
      </dl>
     </div>
     <div className="cp-community-photos">
      <img src={IMG.garden} width="1100" height="733" alt={c.gardenAlt} loading="lazy" />
      <img src={IMG.inside} width="1100" height="828" alt={c.insideAlt} loading="lazy" />
     </div>
    </div>
   </section>

   {/* ---------- FAQ ---------- */}
   <section className="cs-section cs-faq" id="faq" aria-labelledby="cp-faq-title">
    <div className="cs-wrap cs-faq-grid">
     <h2 id="cp-faq-title">{tie(c.faqTitle)}</h2>
     <div className="cs-faq-list">
      {c.faqs.map(([q, a]) => <details key={q}><summary>{tie(q)}</summary><p>{tie(a)}</p></details>)}
     </div>
    </div>
   </section>

   {/* ---------- The partner form ---------- */}
   <section className="cs-section cp-form-section" id="partner" aria-labelledby="cp-form-title">
    <div className="cs-wrap cp-form-grid">
     <div className="cp-form-aside">
      <p className="cs-label">{c.formLabel}</p>
      <h2 id="cp-form-title">{tie(c.formTitle)}</h2>
      <p className="cs-lead">{tie(c.formIntro)}</p>
      <h3 className="cp-next-title">{c.nextTitle}</h3>
      <ol className="cp-next">
       {c.next.map(([title, text], i) => (
        <li key={title}><span aria-hidden="true">{i + 1}</span><div><strong>{title}</strong><p>{text}</p></div></li>
       ))}
      </ol>
      <p className="cp-talk">{c.talk} <a href="mailto:info@city-seek.com">info@city-seek.com</a></p>
     </div>

     <div className="cp-form-card" ref={formTop}>
      {status === 'sent' ? (
       <div className="cp-sent" role="status">
        <span className="cp-sent-tick"><Check /></span>
        <h3>{c.sentTitle}</h3>
        <p>{c.sentText.replace('{email}', sentTo)}</p>
        <button type="button" className="cs-btn cs-btn-ghost" onClick={() => setStatus('idle')}>{c.sentAgain}</button>
       </div>
      ) : (
       <form className="cp-form" onSubmit={submit}>
        <fieldset ref={typeGroup} className={`cp-fieldset${typeMissing ? ' is-missing' : ''}`} disabled={busy}
         aria-describedby={typeMissing ? 'cp-type-error' : undefined}>
         <legend>{c.typeLegend}</legend>
         <div className="cp-type-grid">
          {BUSINESS_TYPES.map(t => (
           <label key={t} className={`cp-type${type === t ? ' is-on' : ''}`}>
            <input type="radio" name="businessType" value={t} checked={type === t}
             onChange={() => { setType(t); setTypeMissing(false); }} />
            <span>{c.types[t][0]}</span>
           </label>
          ))}
         </div>
         {typeMissing && <p className="cp-field-error" id="cp-type-error" role="alert">{c.pickType}</p>}
        </fieldset>

        <fieldset className="cp-fieldset" disabled={busy}>
         <div className="cp-row">
          <label>{c.fieldBusiness}<input name="businessName" required maxLength={160} autoComplete="organization" /></label>
          <label>{c.fieldAddress}<input name="address" required minLength={3} maxLength={400} autoComplete="street-address" /></label>
         </div>
         <div className="cp-row">
          <label>{c.fieldContact}<input name="contactName" required maxLength={120} autoComplete="name" /></label>
          <label>{c.fieldRole}<input name="role" maxLength={80} placeholder={c.rolePlaceholder} autoComplete="organization-title" /></label>
         </div>
         <div className="cp-row">
          <label>{c.fieldEmail}<input name="email" type="email" required maxLength={254} autoComplete="email" /></label>
          <label>{c.fieldPhone}<input name="phone" type="tel" maxLength={40} autoComplete="tel" /></label>
         </div>
         <label>{c.fieldLink}<input name="link" maxLength={200} inputMode="url" placeholder="instagram.com/…" /></label>
        </fieldset>

        <fieldset className="cp-fieldset" disabled={busy}>
         <legend>{c.offerLegend}</legend>
         <div className="cp-plan-grid">
          {(['pair', 'standard', 'custom'] as Plan[]).map(p => (
           <label key={p} className={`cp-plan${plan === p ? ' is-on' : ''}`}>
            <input type="radio" name="plan" value={p} checked={plan === p} onChange={() => setPlan(p)} />
            <strong>{c.plans[p][0]}</strong>
            <span>{c.plans[p][1]}</span>
           </label>
          ))}
         </div>

         {plan === 'pair' && (
          <div className="cp-plan-detail">
           <p className="cp-mini-legend" id="cp-pair-legend">{c.pairDealLegend}</p>
           <div className="cp-segment" role="radiogroup" aria-labelledby="cp-pair-legend">
            {(['half', 'free'] as PairDeal[]).map(d => (
             <label key={d} className={pairDeal === d ? 'is-on' : undefined}>
              <input type="radio" name="pairDeal" value={d} checked={pairDeal === d} onChange={() => setPairDeal(d)} />
              {c.pairDeals[d]}
             </label>
            ))}
           </div>
           <label>{c.pairItemLabel}<input name="pairItem" maxLength={120} placeholder={c.pairItemPlaceholder} /></label>
          </div>
         )}
         {plan === 'standard' && (
          <div className="cp-plan-detail">
           <p className="cp-mini-legend" id="cp-seq-legend">{c.sequenceLegend}</p>
           <div className="cp-segment is-rich is-three" role="radiogroup" aria-labelledby="cp-seq-legend" aria-describedby="cp-seq-hint">
            {SEQUENCES.map(s => (
             <label key={s} className={sequence === s ? 'is-on' : undefined}>
              <input type="radio" name="sequence" value={s} checked={sequence === s} onChange={() => setSequence(s)} />
              <strong>{c.sequences[s][0]}</strong>
              <span>{c.sequences[s][1]}</span>
             </label>
            ))}
           </div>
           <p className="cp-hint" id="cp-seq-hint" aria-live="polite">{sequenceHint}</p>
          </div>
         )}
         {plan === 'custom' && (
          <div className="cp-plan-detail">
           <label>{c.customLabel}<textarea name="offer" rows={3} required minLength={3} maxLength={2000} placeholder={c.customPlaceholder} /></label>
          </div>
         )}

         <div className="cp-limit-field">
          <p className="cp-mini-legend" id="cp-limit-legend">{c.limitLabel}</p>
          <div className="cp-segment is-four" role="radiogroup" aria-labelledby="cp-limit-legend" aria-describedby="cp-limit-hint">
           {PER_PERSON_LIMITS.map(l => (
            <label key={l} className={limit === l ? 'is-on' : undefined}>
             <input type="radio" name="perPersonLimit" value={l} checked={limit === l} onChange={() => setLimit(l)} />
             {c.limitChoices[l]}
            </label>
           ))}
          </div>
          <p className="cp-hint" id="cp-limit-hint" aria-live="polite">{limitHint}</p>
          <p className="cp-rule-line"><Check />{c.comboRule}</p>
         </div>

         <label>{c.notesLabel}<textarea name="notes" rows={2} maxLength={1000} /></label>
         <div className="cp-honeypot" aria-hidden="true">
          <label>Website<input name="website" tabIndex={-1} autoComplete="off" /></label>
         </div>
        </fieldset>

        <label className="cp-consent">
         <input type="checkbox" name="consent" required disabled={busy} />
         <span>{c.consent} <a href="/privacy" target="_blank" rel="noopener noreferrer">{c.privacy}</a></span>
        </label>

        {status === 'error' && <p className="cp-field-error" role="alert">{c.sendError}</p>}

        <div className="cp-submit">
         <button type="submit" className="cs-btn" disabled={busy}>{busy ? c.sending : c.submit}</button>
         <span>{c.formNote}</span>
        </div>
       </form>
      )}
     </div>
    </div>
   </section>

   {/* ---------- Final call ---------- */}
   <section className="cs-final" aria-labelledby="cp-final-title">
    <div className="cs-wrap">
     <h2 id="cp-final-title">{tie(c.finalTitle)}</h2>
     <p>{tie(c.finalText)}</p>
     <div className="cs-hero-actions">
      <a className="cs-btn" href="#partner">{c.primary}</a>
      <a className="cs-btn cs-btn-ghost" href={app('/app/coupons?view=map', language)}>{c.finalSecondary}</a>
     </div>
    </div>
   </section>
  </main>

  <footer className="cs-footer">
   <div className="cs-wrap cs-footer-inner">
    <a className="cs-logo" href={HOME_PATH[language]} aria-label="CitySeek"><Wordmark /></a>
    <p>{c.rights} © {new Date().getFullYear()} CitySeek · {c.where}</p>
    <a href="mailto:info@city-seek.com">info@city-seek.com</a>
    <nav aria-label={c.languages}>
     {LANGS.map(code => <a key={code} href={COUPONS_PATH[code]} hrefLang={code}>{languageNames[code]}</a>)}
    </nav>
    <nav aria-label="Legal">
     <a href={HOME_PATH[language]}>{c.forEveryone}</a>
     {c.legal.map((label, i) => <a key={label} href={['/impressum', '/privacy', '/terms', '/cookies'][i]}>{label}</a>)}
    </nav>
   </div>
  </footer>

  <div className={`cp-sticky${sticky ? ' is-on' : ''}`} aria-hidden={!sticky}>
   <a className="cs-btn" href="#partner" tabIndex={sticky ? 0 : -1}>{c.stickyCta}</a>
  </div>
 </div>;
}
